import { Link, useLocation } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'

export function NavMain({
  groups,
}: {
  groups: {
    label: string
    items: {
      title: string
      url?: string
      icon: React.ReactNode
      isActive?: boolean
      items?: {
        title: string
        url: string
      }[]
    }[]
  }[]
}) {
  return (
    <>
      {groups.map((group) => (
        <SidebarGroup key={group.label}>
          <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
          <SidebarMenu>
            {group.items.map((item) => (
              <NavMainItem
                key={item.title}
                item={item}
              />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  )
}

function NavMainItem({
  item,
}: {
  item: {
    title: string
    url?: string
    icon: React.ReactNode
    isActive?: boolean
    items?: {
      title: string
      url: string
    }[]
  }
}) {
  const location = useLocation()
  const isChildActive = !!item.items?.some((subItem) =>
    location.pathname.startsWith(subItem.url),
  )
  const isParentActive = !!(
    (item.url && item.url === '/admin'
      ? location.pathname === '/admin'
      : item.url && location.pathname.startsWith(item.url)) || isChildActive
  )

  const [open, setOpen] = useState(!!(item.isActive || isParentActive))

  // Sync open state when navigation happens to this item
  useEffect(() => {
    if (isParentActive) {
      setOpen(true)
    }
  }, [isParentActive])

  const menuButton = (
    <SidebarMenuButton
      tooltip={item.title}
      render={item.url ? <Link to={item.url as any} /> : undefined}
      isActive={isParentActive}
    >
      {item.icon}
      <span>{item.title}</span>
      {item.items?.length && !item.url && (
        <ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
      )}
    </SidebarMenuButton>
  )

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      render={<SidebarMenuItem />}
    >
      {item.items?.length && !item.url ? (
        <CollapsibleTrigger render={menuButton} />
      ) : (
        menuButton
      )}
      {item.items?.length ? (
        <>
          {item.url && (
            <CollapsibleTrigger
              render={<SidebarMenuAction className="aria-expanded:rotate-90" />}
            >
              <ChevronRightIcon />
              <span className="sr-only">Toggle</span>
            </CollapsibleTrigger>
          )}
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.items?.map((subItem) => (
                <SidebarMenuSubItem key={subItem.title}>
                  <SidebarMenuSubButton
                    render={<Link to={subItem.url as any} />}
                    isActive={location.pathname === subItem.url}
                  >
                    <span>{subItem.title}</span>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </>
      ) : null}
    </Collapsible>
  )
}
